import { DOCUMENT } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, NgZone } from '@angular/core';
import { catchError, map, Observable, of, tap } from 'rxjs';
import {
  applySkynetLocale,
  isSameSkynetLocale,
  SKYNET_SCRIPT_ID,
  SKYNET_SET_USER_SCRIPT_ID,
  SkynetLocale,
  skynetLocale,
} from '@common/skynet-locale';
import { Auth } from '@core/entity/auth/model/auth.model';
import { DomainService } from '@core/services/domain.service';
import { EventEmitterService } from '@core/services/event-emitter.service';
import { PlatformService } from '@core/services/platform.service';
import { SKYNET_BOTTOM_OFFSET } from '@core/services/skynet-ui.config';
import { ApiResponse } from '@shared/models/api-response.model';
import { AuthTokenKey } from '@shared/local-storage.key';

/** What BO configured, as `/livechat/skynetsettings` answers it. */
interface SkynetDetails {
  status: boolean;
  embedKey: string;
  endpoint: string;
}

/**
 * The Skynet chat widget (AIO-82885 — QPRO's AIO-81902 / AIO-83314, ported).
 *
 * ── IT REPLACES LIVE CHAT, IT DOES NOT SIT BESIDE IT ──────────────────────
 *
 * `/livechat/skynetsettings` is the switch: configured, Skynet is the chat and BO's live-chat
 * script is never fetched; unconfigured, nothing here happens and AppComponent's existing
 * LiveChat/Crisp path runs exactly as before. That either/or is SupportChatService's job —
 * the two providers must never both draw a launcher.
 *
 * ── AND THE WIDGET IS LOADED BY AN INJECTED SCRIPT, NOT BY US ─────────────
 *
 * The vendor's `widget.js` is fetched by a snippet written into the page, the same shape
 * `loadClarity()` and `recaptcha.service.ts` already use here. Three things in it are
 * deliberate:
 *
 *   · every value crosses into that snippet through `JSON.stringify`, never inside bare
 *     quotes. They come from a settings row BO edits, and one quote in one of them would
 *     close the string literal and run the rest as code in every visitor's browser.
 *     `JSON.stringify` can only ever emit inert data.
 *   · the snippet concatenates strings rather than nesting template literals. A nested
 *     backtick inside a TypeScript template literal is a whole class of silent breakage
 *     whose errors point everywhere except the real line.
 *   · `init()` is explicit — Skynet does NOT auto-boot from the settings object — and it is
 *     called from `onload`, so nothing long-lived is created here. A timer would sit in
 *     NgZone and stall `applicationRef.whenStable()`.
 *
 * Browser only, in every method: the widget is third-party DOM, and the server has neither a
 * document to put it in nor a member to identify.
 */
@Injectable({
  providedIn: 'root'
})
export class SkynetService {

  /** Skynet is configured AND its widget snippet has been put in the page. */
  private active: boolean = false;

  private details: SkynetDetails = null;

  /** The language + country the widget was last given; null until `install()`. */
  private sentLocale: SkynetLocale = null;

  /** Bumped by `forget()` and by each `identify()`, so a response in flight can be cancelled. */
  private identifyEpoch: number = 0;

  constructor(
    private http: HttpClient,
    @Inject(DOCUMENT) private document: Document,
    private platformService: PlatformService,
    private domainService: DomainService,
    private eventEmitterService: EventEmitterService,
    private ngZone: NgZone,
  ) {
    /* A language switch — or a sign-in moving the site to the member's own locale — moves the
       widget with it. `localeUpdated` and NOT `countryUpdated`: DomainService compares an
       UPPERCASE current code against a lowercase new one (domain.service.ts:288-292), so
       languageUpdated and countryUpdated both fire on EVERY locale change whether or not that
       half moved, and acting on countryUpdated would re-mount on a pure language switch and
       throw away the open chat. `syncLocale` decides what really changed. Before install()
       this is a no-op, and install() itself records the locale it used. */
    if (this.platformService.isBrowser()) {
      this.eventEmitterService.localeUpdated.subscribe(() => this.syncLocale());
    }
  }

  isActive(): boolean {
    return this.active;
  }

  /**
   * Ask BO whether Skynet is the chat for this brand.
   *
   * Answers TRUE when the caller should hand chat over to Skynet, so SupportChatService can
   * start LiveChat/Crisp instead on a false. A failed request answers false for the same
   * reason: an unreachable settings endpoint must leave the visitor with BO's live chat, not
   * with nothing.
   *
   * Never cached. All four QP2 brands share one api2 deployment and one database, separated
   * only by the `X-Site-Prefix` header the interceptor appends, and each brand has its own
   * embed key.
   */
  load(): Observable<boolean> {
    if (!this.platformService.isBrowser()) {
      return of(false);
    }

    return this.http.get<ApiResponse>('/livechat/skynetsettings').pipe(
      map(res => {
        const data = (res?.data ?? {}) as Record<string, unknown>;

        return {
          status: isOn(data['status']),
          embedKey: String(data['embed_key'] ?? ''),
          endpoint: String(data['endpoint'] ?? ''),
        } as SkynetDetails;
      }),
      tap(details => this.details = details),
      /* `status` alone is not enough — the two credentials are what make the widget
         loadable, and an endpoint-less widget calls our own site and says
         "Connection unavailable" (guide §3). */
      map(details => details.status && !!details.embedKey && !!details.endpoint),
      catchError(() => of(false)),
    );
  }

  /** Put the widget in the page. Call only after `load()` answered true. */
  install(): void {
    if (!this.platformService.isBrowser() || !this.details || this.active) {
      return;
    }

    this.sentLocale = skynetLocale(this.domainService.getLanguageCode(), this.domainService.getCountryCode());

    const settings = {
      embed_key: this.details.embedKey,
      endpoint: this.details.endpoint,
      ...this.sentLocale,
      /* Keeps the launcher off the fixed bottom bar; per source tree, see skynet-ui.config.ts.
         Spread conditionally rather than sending 0 or null: an explicit value here WINS over
         whatever a Skynet admin set in their console (guide §5), so a tree with nothing to
         clear sends nothing at all. */
      ...(SKYNET_BOTTOM_OFFSET === null ? {} : { bottomOffset: SKYNET_BOTTOM_OFFSET }),
    };
    const script = this.document.createElement('script');

    script.id = SKYNET_SCRIPT_ID;
    script.text = `
      ;(function () {
        window.$chatWidgetSettings = ${JSON.stringify(settings)};
        var script = document.createElement('script');
        script.src = window.$chatWidgetSettings.endpoint + '/widget.js';
        script.async = true;
        script.onload = function () {
          if (!window.$chatWidget || typeof window.$chatWidget.init !== 'function') { return; }
          /* init() may return a promise, so a bare try/catch would only see a synchronous
             throw and a rejection would escape unhandled. Both handlers are empty on
             purpose: a widget that cannot start is simply absent, and nothing is shown to
             the visitor either way. */
          try {
            Promise.resolve(window.$chatWidget.init(window.$chatWidgetSettings)).catch(function () { });
            /* The widget prefers the language saved in its own storage over
               settings.language — the page wins. */
            if (typeof window.$chatWidget.setLanguage === 'function') {
              Promise.resolve(window.$chatWidget.setLanguage(window.$chatWidgetSettings.language)).catch(function () { });
            }
          } catch (e) { }
        };
        document.head.appendChild(script);
      })();
    `;

    /* head, not body: matches loadClarity() and keeps BaseInnerComponent's existing
       MutationObserver on document.head working for the RTP badge.

       ⚠️ OUTSIDE THE ANGULAR ZONE. An inline script created with createElement + .text runs
       SYNCHRONOUSLY inside appendChild, so everything it starts — here widget.js's own load,
       and in turn the widget's socket and timers — inherits Zone.current. zone.js patches
       setInterval/setTimeout/XHR and captures the zone at call time, so a third-party widget
       booted inside the Angular zone keeps ApplicationRef.isStable() false for as long as it
       is connected. Desktop registers its service worker with `registerWhenStable:30000`. */
    this.ngZone.runOutsideAngular(() => this.document.head.appendChild(script));
    this.active = true;
  }

  /**
   * Move a running widget to the page's current locale.
   *
   * A re-mounted widget starts anonymous, so the member is attached again. Never `forget()`
   * here — that is sign-out, and its `reset()` would drop the open chat.
   */
  private syncLocale(): void {
    if (!this.platformService.isBrowser() || !this.active || !this.details || !this.sentLocale) {
      return;
    }

    const next = skynetLocale(this.domainService.getLanguageCode(), this.domainService.getCountryCode());

    if (isSameSkynetLocale(next, this.sentLocale)) {
      return;
    }

    const previous = this.sentLocale;

    /* The re-mount calls the vendor's init(), which opens a socket — same zone reasoning as
       install(). */
    const remounted = this.ngZone.runOutsideAngular(() => applySkynetLocale(
      this.document.defaultView,
      this.document,
      { embed_key: this.details.embedKey, endpoint: this.details.endpoint, ...next },
      previous,
    ));

    /* ⚠️ RECORD ONLY WHAT ACTUALLY MOVED. applySkynetLocale answers false both when it
       switched the language in place (a success) and when the replacement failed to mount (a
       failure, where the running widget keeps its OLD country). Committing `next` in both
       cases would leave this service believing a country change happened that did not, so no
       later locale event could ever retry it — the chat would stay on the wrong region's CS
       team for the rest of the page load, with nothing on screen to say so. */
    if (remounted || next.country === previous.country) {
      this.sentLocale = next;
    } else {
      /* The language still switched in place; the country did not. */
      this.sentLocale = { ...previous, language: next.language };
    }

    if (remounted) {
      /* A re-mounted widget starts anonymous. */
      this.identify();
    }
  }

  /**
   * Show or hide the chat window. Answers FALSE when there is no widget to open.
   *
   * `install()` having run is NOT the same as `widget.js` having LOADED — a 404, a blocked
   * third-party script or a dead `endpoint` all leave the global absent — and `active`
   * deliberately tracks the former, because that is what decides live chat must not also
   * start. The caller says what happens next; see SupportChatService.
   */
  open(): boolean {
    const widget = chatWidget();

    /* open(), not toggle(): the control this replaced called LiveChatWidget.call('maximize') /
       Crisp.chat.open(), both strictly OPEN, and toggle() would CLOSE the chat for a visitor
       who already has it open. toggle() stays as a fallback for a bundle without open(). */
    if (widget && typeof widget.open === 'function') {
      widget.open();

      return true;
    }

    if (widget && typeof widget.toggle === 'function') {
      widget.toggle();

      return true;
    }

    return false;
  }

  /**
   * Attach the signed-in member to the widget, so an agent sees who they are.
   *
   * ⚠️ THE WAIT HAPPENS INSIDE AN INJECTED SCRIPT, NOT IN AN ANGULAR TIMER.
   * `widget.js` is loaded asynchronously by `install()`, so on a page that boots already
   * signed in the JWT can arrive before the global exists and a bare call would throw. A
   * long-lived timer on the Angular side would sit in NgZone and stall
   * `applicationRef.whenStable()`, which leaves `@defer` blocks unhydrated on Safari. Bounded
   * at ~10s (50 × 200ms), and the element re-check makes a sign-out during the wait cancel
   * the pending call.
   *
   * Called on every page load rather than on a timer: that is the guide's own Flow D for an
   * expired pass, and `AuthEffects.login$` fires again on localStorage rehydration.
   */
  identify(): void {
    const token = this.sessionToken();

    if (!this.platformService.isBrowser() || !this.active || !token) {
      return;
    }

    /* ~30 setUser calls per minute per IP, shared with chat file uploads, and visitors behind
       one office NAT share the address. One attach per sign-in, never one per render. */
    if (this.document.getElementById(SKYNET_SET_USER_SCRIPT_ID)) {
      return;
    }

    const epoch = ++this.identifyEpoch;

    this.http.get<ApiResponse>('/skynet/chatpass').subscribe({
      next: res => {
        const jwt = String(res?.data?.jwt ?? '');

        /* ⚠️ THE SESSION CAN END WHILE THIS REQUEST IS IN FLIGHT, and forget() only removes
           the element that exists WHEN IT RUNS. A response landing after a sign-out would
           append the script regardless, and the widget would re-attach the member who just
           left — on a shared device that is exactly the cross-account leak reset() exists to
           prevent. So re-read the session rather than trusting what this closure captured,
           and check the epoch, which forget() bumps. An empty jwt means api2 is unconfigured
           or something failed: chat anonymously, which still works. */
        if (!jwt || epoch !== this.identifyEpoch || this.sessionToken() !== token) {
          return;
        }

        const script = this.document.createElement('script');

        script.id = SKYNET_SET_USER_SCRIPT_ID;
        script.text = `
          (function () {
            var attempts = 0;
            var timer = setInterval(function () {
              if (!document.getElementById(${JSON.stringify(SKYNET_SET_USER_SCRIPT_ID)})) { clearInterval(timer); return; }
              if (window.$chatWidget && typeof window.$chatWidget.setUser === 'function') {
                clearInterval(timer);
                /* setUser() can reject with any of Skynet's eight documented codes (not
                   RS256, expired pass, rate limited, unknown embed key, origin not allowed,
                   ...). Degrade silently to anonymous chat: the widget stays usable, the
                   member is simply not attached. */
                Promise.resolve(window.$chatWidget.setUser(${JSON.stringify(jwt)})).catch(function () { });
              } else if (++attempts > 50) {
                clearInterval(timer);
              }
            }, 200);
          })();
        `;

        /* ⚠️ OUTSIDE THE ANGULAR ZONE — see install(). This callback runs inside HttpClient's
           subscribe, so a bare appendChild would execute the snippet in the Angular zone and
           its setInterval would become a PENDING MACROTASK there: isStable() never settles
           while the poll runs, and change detection ticks every 200ms for up to 10s. Putting
           the wait in an injected script is not on its own enough to escape the zone. */
        this.ngZone.runOutsideAngular(() => this.document.head.appendChild(script));
      },
      /* No pass means anonymous chat, which still works. */
      error: () => undefined,
    });
  }

  /**
   * Sign-out: drop the identity the widget is holding.
   *
   * ⚠️ `reset()`, NOT `clear()`. Guide §4: `clear()` is newConversation() — it starts a new
   * conversation but leaves the session ticket in the widget's own localStorage, so the next
   * page load RESUMES the signed-out member's identified session. On a shared computer that
   * is a cross-account leak, and it is go-live checklist item 7. `reset()` disconnects, nulls
   * the ticket, clears that storage and reconnects anonymously, with the widget still mounted
   * so nothing needs re-injecting.
   *
   * Deliberately NOT gated on `active`: that flag and the setUser element both describe THIS
   * page session, but the widget restores a previous member's session from its own storage on
   * init() — so a page that loads already signed out would otherwise never clear it.
   */
  forget(): void {
    if (!this.platformService.isBrowser()) {
      return;
    }

    /* Cancels any chat pass still in flight — see identify(). */
    this.identifyEpoch++;

    this.document.getElementById(SKYNET_SET_USER_SCRIPT_ID)?.remove();
    chatWidget()?.reset?.();
  }
  /**
   * The member's access token, or null for a guest.
   *
   * Read from localStorage, and from the same two keys in the same order as apiInterceptor
   * (api.interceptor.ts:103-122): AuthTokenKey, written by the ngrx login effect, then the
   * legacy 'user_token', written by the cross-login / auto-login URL-param paths. The store
   * is empty on a fresh page load until the effect rehydrates, so reading it instead would
   * make this depend on navigation order — and this has to agree with the interceptor about
   * whether the request will carry session headers at all.
   */
  private sessionToken(): string | null {
    if (!this.platformService.isBrowser()) {
      return null;
    }

    for (const key of [AuthTokenKey, 'user_token']) {
      try {
        const auth = JSON.parse(localStorage.getItem(key) || '{}') as Auth;

        if (auth?.access_token && auth?.plaintext_token) {
          return auth.access_token;
        }
      } catch {
        /* A corrupt entry is a guest, same as an absent one. */
      }
    }

    return null;
  }
}

/**
 * ⚠️ THE API SENDS `1`, NOT `true`.
 *
 * The column is `tinyint(1)` and the model declares no cast, so `status` reaches the client
 * as the raw column. QPRO shipped a strict `=== true` against this exact response, which
 * silently disabled the widget for ever and sent every brand to live chat instead — caught
 * against the live endpoint after mocked tests passed on a `true` the api never sends.
 *
 * Written out rather than `== true` so the shapes are explicit and a string `"1"` — which a
 * different driver or a PDO fetch mode can produce — is covered too.
 */
function isOn(value: unknown): boolean {
  return value === true || value === 1 || value === '1';
}

/** The vendor's global, once `widget.js` has run. */
function chatWidget(): ChatWidgetGlobal | undefined {
  return (globalThis as { $chatWidget?: ChatWidgetGlobal }).$chatWidget;
}

/* `clear()` is deliberately absent: it must never be called (see forget()), and leaving it off
   this type means the compiler refuses it too. */
interface ChatWidgetGlobal {
  open?: () => void;
  toggle?: () => void;
  reset?: () => void;
}
